import { HomeAccountListingDetailsModel, PropertySummary } from 'api/models';
import { find, isNil } from 'es-toolkit/compat';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import HomeownerAccountUnitDetail from 'models/homeownerAccounts/HomeownerAccountUnitDetail';
import { createSelector } from 'reselect';
import { AppState } from '../types';
import { HOMEOWNER_ACCOUNTS_REDUCER } from './reducer';

const _selectHomeownerAccounts = (state: AppState) =>
  state[HOMEOWNER_ACCOUNTS_REDUCER];
export const selectAccountPropertySummaries = (
  state: AppState,
): PropertySummary[] => _selectHomeownerAccounts(state).properties;
export const selectAccountUnitListings = (
  state: AppState,
): HomeAccountListingDetailsModel[] => _selectHomeownerAccounts(state).listings;

// `basicInfo` and `units` are optional on `PropertySummary`, but every read in
// this file already dereferenced them unconditionally. The assertions state
// that; they are erased at runtime, so a summary missing one still throws
// exactly where it did before.
const _selectAccountPropertyById = (state: AppState, id: string) =>
  find(
    selectAccountPropertySummaries(state),
    (property) => property.basicInfo!.propertyId === id,
  );

const _selectAccountUnits = (state: AppState) =>
  _selectHomeownerAccounts(state).units;
const _selectAccountUnitById = (state: AppState, id: string) =>
  _selectAccountUnits(state)[id];

export const selectAccountProperties = createSelector(
  selectAccountPropertySummaries,
  (properties) =>
    properties
      ? properties.map((property) => new HomeownerAccountProperty(property))
      : null,
);

export const selectAccountPropertyById = createSelector(
  _selectAccountPropertyById,
  (property) => new HomeownerAccountProperty(property),
);

// `unitId` is optional because callers read it off `homeId`, which is optional
// on the home models. A `undefined` id matches nothing and the selector returns
// `null`, which is what it did before.
export const selectAccountPropertyByUnitId = (unitId?: string) =>
  createSelector(selectAccountPropertySummaries, (properties) => {
    if (properties) {
      const propertyFound = properties.find((property) =>
        property.units!.some((unit) => unit.basicInfo!.unitId === unitId),
      );

      return propertyFound || null;
    }
    return null;
  });

export const selectAccountUnitByUnitId = (unitId?: string) =>
  createSelector(selectAccountPropertySummaries, (properties) => {
    if (properties) {
      const propertyFound = properties.find((property) =>
        property.units!.some((unit) => unit.basicInfo!.unitId === unitId),
      );
      // `propertyFound` was matched by the `.some()` above, so its `units` is
      // the same array that predicate just iterated.
      const unitFound = propertyFound?.units!.find(
        (unit) => unit.basicInfo!.unitId === unitId,
      );

      return unitFound || null;
    }
    return null;
  });

export const selectAccountUnitsValues = createSelector(
  _selectAccountUnits,
  (units) =>
    Object.values(units).map((unitDetail) =>
      unitDetail ? new HomeownerAccountUnitDetail(unitDetail) : null,
    ),
);

export const selectAccountUnitExternalPublishings = createSelector(
  _selectAccountUnits,
  (units) =>
    // Annotated because `Object.values` over an untyped map yields `unknown[]`,
    // so every read off the entry was an error.
    Object.values(units).map((unit: any) => ({
      unitId: unit.basicInfo.unitId,
      externalListingPublishings: unit.basicInfo.externalListingPublishings,
    })),
);

export const selectAccountAgreements = (state: AppState) =>
  _selectHomeownerAccounts(state).agreements;

const _selectTimelines = (state: AppState) =>
  _selectHomeownerAccounts(state).timelines;

export const selectTimelines = createSelector(_selectTimelines, (timelines) => {
  // The timeline slice is shaped by untyped JS actions, so the accumulators and
  // entries below are `any` for the same reason `AppState` is.
  return timelines.reduce((acc: any, timeline: any) => {
    // Normalize by last event Id to make it easy to match with the corresponding event.
    const normalizeMilestonesByLastEventId = timeline.milestones.reduce(
      (innerAcc: any, val: any) => {
        if (innerAcc[val.latestTimelineEventId]) {
          innerAcc[val.latestTimelineEventId].push(val);
        } else {
          innerAcc[val.latestTimelineEventId] = [val];
        }
        return innerAcc;
      },
      {},
    );

    const normalizeRequiredEventsByName =
      timeline?.requiredEventsForNextMilestone?.reduce(
        (innerAcc: any, val: any) => {
          return { ...innerAcc, [val.timelineEventType]: val };
        },
        {},
      );

    const timelineEvents = timeline.timelineEvents.map((event: any) => {
      // Insert milestone into event that triggered the milestone if exists.
      if (normalizeMilestonesByLastEventId[event.id]) {
        event.milestones = normalizeMilestonesByLastEventId[event.id];
      }

      return event;
    });

    // homeId is optional per-milestone, so key off the first milestone that
    // has one (mirrors the old lookup, which matched against every milestone).
    // Skip timelines with no identifying milestone instead of crashing.
    const homeId = find(
      timeline.milestones,
      (milestone: any) => !isNil(milestone.homeId),
    )?.homeId;

    if (isNil(homeId)) {
      return acc;
    }

    return {
      ...acc,
      [homeId]: {
        nextMilestone: timeline.nextMilestone,
        requiredEventsForNextMilestone: normalizeRequiredEventsByName,
        timelineEvents,
      },
    };
  }, {});
});

export const selectHomeAccessInstructions = (state: AppState, unitId: string) =>
  _selectHomeownerAccounts(state).homeAccessInstructions[unitId];

export const selectUpcomingAppointments = (state: AppState) =>
  _selectHomeownerAccounts(state).upcomingAppointments;

export const selectWealth = (state: AppState) =>
  _selectHomeownerAccounts(state).wealth;
